import { useI18n } from "@/i18n/I18nContext";

export const SUBS = ["mongo", "storage", "queue", "dlreg", "stripe", "sandboxes", "messaging", "purge", "capacity", "translations", "email_push"];
export const tone = (g) => (g >= 99.99 ? "text-emerald-300" : g >= 90 ? "text-amber-300" : "text-red-300");
export const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString(document.documentElement.lang || "fr", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
export const ms = (v) => (v == null ? "—" : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${Math.round(v)} ms`);

const Spark = ({ hist }) => {
  if (!hist?.length) return null;
  const min = Math.min(90, ...hist);
  const pts = hist.map((g, i) => `${(i / Math.max(1, hist.length - 1)) * 100},${30 - ((g - min) / Math.max(0.0001, 100 - min)) * 28}`).join(" ");
  return (
    <svg viewBox="0 0 100 32" preserveAspectRatio="none" className="mt-4 h-10 w-full" aria-hidden="true">
      <polyline points={pts} fill="none" stroke="var(--gold)" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export const HealthGauge = ({ d }) => {
  const { t } = useI18n();
  const ok = d.gauge >= 99.99;
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5 sm:p-6" data-testid="health-gauge-card">
      <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("heal.gauge")}</p>
      <p className={`mt-2 break-all font-display text-4xl font-bold tabular-nums sm:text-5xl ${tone(d.gauge)}`} data-testid="health-gauge-value">
        <bdi>{d.gauge.toFixed(4)} %</bdi>
      </p>
      <p className="mt-2 text-sm text-neutral-300" data-testid="health-gauge-status">{t(ok ? "heal.ok" : "heal.degraded")}</p>
      <Spark hist={d.hist} />
      <p className="mt-2 text-xs text-neutral-500" data-testid="health-last-cycle">{t("heal.lastCycle", { t: timeOf(d.last_full) })}</p>
      {d.alert && <p className="mt-3 rounded-lg border border-red-500/40 bg-red-950/50 p-2 text-xs text-red-100" role="alert" data-testid="health-alert">{d.alert.text}</p>}
    </section>
  );
};

export const SubsystemList = ({ subs }) => {
  const { t } = useI18n();
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="health-subsystems">
      <h2 className="font-display text-base font-bold text-white md:text-lg">{t("heal.subsystems")}</h2>
      <ul className="mt-3 space-y-2">
        {subs.map((s) => (
          <li key={s.key} className="min-w-0" data-testid={`health-sub-${s.key}`}>
            <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 text-sm">
              <span className="min-w-0 text-neutral-200">{t(`heal.sub.${s.key}`)} <span className="text-xs text-neutral-500">· {t("heal.weight")} {s.weight}</span></span>
              <bdi className={`tabular-nums ${tone(s.health * 100)}`} data-testid={`health-sub-score-${s.key}`}>{s.score.toFixed(4)} / {s.weight}</bdi>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-800">
              <div className={`h-full ${s.health >= 0.9999 ? "bg-emerald-500" : s.health >= 0.5 ? "bg-amber-500" : "bg-red-600"} transition-[width] duration-500`} style={{ width: `${s.health * 100}%` }} />
            </div>
            {s.issues.length > 0 && (
              <p className="mt-1 break-words text-xs text-amber-200" data-testid={`health-sub-issues-${s.key}`}>{s.issues.map((i) => t(`heal.code.${i.code}`)).join(" · ")}</p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};
